import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Stack, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Tooltip, CircularProgress, Divider, Tab, Tabs,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  Transform as ConvertIcon,
  LightbulbOutlined as IdeaIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { ideasApi } from '../api/services';
import type { Idea, IdeaStatus } from '../types/models';
import DictationTextField from '../components/DictationTextField';

const STATUS_COLOURS: Record<IdeaStatus, string> = {
  Captured: 'rgba(108, 155, 255, 0.25)',
  Assessed: 'rgba(77, 208, 225, 0.25)',
  Converted: 'rgba(129, 199, 132, 0.25)',
  Archived: 'rgba(255,255,255,0.06)',
};

const STATUS_LABELS: Record<IdeaStatus, string> = {
  Captured: 'Captured',
  Assessed: 'Assessed',
  Converted: 'Converted',
  Archived: 'Archived',
};

type TabFilter = 'Active' | 'Converted' | 'Archived';

export default function IdeasPage() {
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabFilter, setTabFilter] = useState<TabFilter>('Active');

  // Capture dialog
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captureTitle, setCaptureTitle] = useState('');
  const [captureContent, setCaptureContent] = useState('');
  const [saving, setSaving] = useState(false);

  // Selected idea detail
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [assessing, setAssessing] = useState(false);
  const [assessError, setAssessError] = useState<string | null>(null);

  // Convert dialog
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertTitle, setConvertTitle] = useState('');
  const [convertPriority, setConvertPriority] = useState('Medium');
  const [convertMinutes, setConvertMinutes] = useState('30');
  const [converting, setConverting] = useState(false);

  const loadIdeas = useCallback(async () => {
    setLoading(true);
    try {
      setIdeas(await ideasApi.getAll());
    } catch (err) {
      console.error('Failed to load ideas', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadIdeas(); }, [loadIdeas]);

  const handleCapture = async () => {
    if (!captureTitle.trim()) return;
    setSaving(true);
    try {
      const idea = await ideasApi.create({ title: captureTitle.trim(), content: captureContent.trim() || undefined });
      setIdeas(prev => [idea, ...prev]);
      setCaptureOpen(false);
      setCaptureTitle('');
      setCaptureContent('');
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this idea?')) return;
    try {
      await ideasApi.delete(id);
      setIdeas(prev => prev.filter(i => i.id !== id));
      if (selectedIdea?.id === id) setDetailOpen(false);
    } catch { /* ignore */ }
  };

  const handleArchive = async (idea: Idea) => {
    try {
      const updated = await ideasApi.update(idea.id, { status: 'Archived' });
      setIdeas(prev => prev.map(i => i.id === idea.id ? updated : i));
      if (selectedIdea?.id === idea.id) setSelectedIdea(updated);
    } catch { /* ignore */ }
  };

  const handleAssess = async () => {
    if (!selectedIdea) return;
    setAssessing(true);
    setAssessError(null);
    try {
      const updated = await ideasApi.assess(selectedIdea.id);
      setIdeas(prev => prev.map(i => i.id === updated.id ? updated : i));
      setSelectedIdea(updated);
    } catch {
      setAssessError('AI assessment failed. Please try again.');
    } finally {
      setAssessing(false);
    }
  };

  const openConvert = (idea: Idea) => {
    setSelectedIdea(idea);
    setConvertTitle(idea.title);
    setConvertPriority('Medium');
    setConvertMinutes('30');
    setConvertOpen(true);
  };

  const handleConvert = async () => {
    if (!selectedIdea) return;
    setConverting(true);
    try {
      await ideasApi.convert(selectedIdea.id, {
        title: convertTitle,
        priority: convertPriority,
        estimatedMinutes: parseInt(convertMinutes) || 30,
      });
      const updated = await ideasApi.getById(selectedIdea.id);
      setIdeas(prev => prev.map(i => i.id === updated.id ? updated : i));
      setSelectedIdea(updated);
      setConvertOpen(false);
      setDetailOpen(false);
    } catch { /* ignore */ }
    finally { setConverting(false); }
  };

  const openDetail = (idea: Idea) => {
    setSelectedIdea(idea);
    setAssessError(null);
    setDetailOpen(true);
  };

  const filteredIdeas = ideas.filter(i => {
    if (tabFilter === 'Active') return i.status === 'Captured' || i.status === 'Assessed';
    if (tabFilter === 'Converted') return i.status === 'Converted';
    return i.status === 'Archived';
  });

  const activeCount = ideas.filter(i => i.status === 'Captured' || i.status === 'Assessed').length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Ideas</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Capture thoughts, assess strategic alignment, convert to work items
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCaptureOpen(true)}>
          Capture Idea
        </Button>
      </Box>

      {/* Stats */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Active Ideas</Typography>
            <Typography variant="h5">{activeCount}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Assessed</Typography>
            <Typography variant="h5">{ideas.filter(i => i.status === 'Assessed').length}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Converted to Work Items</Typography>
            <Typography variant="h5">{ideas.filter(i => i.status === 'Converted').length}</Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={tabFilter} onChange={(_, v) => setTabFilter(v)} sx={{ mb: 2 }}>
        <Tab label={`Active (${activeCount})`} value="Active" />
        <Tab label="Converted" value="Converted" />
        <Tab label="Archived" value="Archived" />
      </Tabs>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && filteredIdeas.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <IdeaIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {tabFilter === 'Active' ? 'No ideas yet — capture your first one' : `No ${tabFilter.toLowerCase()} ideas`}
          </Typography>
        </Box>
      )}

      <Stack spacing={1.5}>
        {filteredIdeas.map(idea => (
          <Card
            key={idea.id}
            sx={{
              cursor: 'pointer',
              border: 1,
              borderColor: 'divider',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(108,155,255,0.04)' },
              transition: 'all 0.15s',
            }}
            onClick={() => openDetail(idea)}
          >
            <CardContent sx={{ pb: '12px !important' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" fontWeight={500} noWrap>
                    {idea.title}
                  </Typography>
                  {idea.content && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {idea.content}
                    </Typography>
                  )}
                  {idea.aiAlignmentAssessment && (
                    <Typography variant="caption" color="primary.main" sx={{ mt: 0.5, display: 'block' }}>
                      {idea.aiAlignmentAssessment.substring(0, 120)}…
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                    <Chip
                      label={STATUS_LABELS[idea.status]}
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem', bgcolor: STATUS_COLOURS[idea.status] }}
                    />
                    {idea.suggestedDriver && (
                      <Chip
                        label={idea.suggestedDriver.title}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(129,199,132,0.2)' }}
                      />
                    )}
                    {idea.suggestedProject && (
                      <Chip
                        label={idea.suggestedProject.title}
                        size="small"
                        sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'rgba(255,183,77,0.2)' }}
                      />
                    )}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={0.5} onClick={e => e.stopPropagation()}>
                  {(idea.status === 'Captured' || idea.status === 'Assessed') && (
                    <Tooltip title="Convert to work item">
                      <IconButton size="small" onClick={() => openConvert(idea)}>
                        <ConvertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {idea.status !== 'Archived' && idea.status !== 'Converted' && (
                    <Tooltip title="Archive">
                      <IconButton size="small" onClick={() => handleArchive(idea)}>
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(idea.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Capture dialog */}
      <Dialog open={captureOpen} onClose={() => setCaptureOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Capture Idea</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              value={captureTitle}
              onChange={e => setCaptureTitle(e.target.value)}
              fullWidth
              size="small"
              placeholder="What's the idea?"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCapture(); } }}
            />
            <DictationTextField
              label="Detail (optional)"
              value={captureContent}
              onChange={(e) => setCaptureContent(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={3}
              placeholder="Add context, motivation, or initial thinking… (mic icon dictates)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCaptureOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCapture} disabled={saving || !captureTitle.trim()}>
            {saving ? 'Saving…' : 'Capture'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Idea detail / assess dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        {selectedIdea && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">{selectedIdea.title}</Typography>
                <Chip
                  label={STATUS_LABELS[selectedIdea.status]}
                  size="small"
                  sx={{ bgcolor: STATUS_COLOURS[selectedIdea.status] }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                {selectedIdea.content && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedIdea.content}
                  </Typography>
                )}

                <Divider />

                {/* AI Assessment section */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    AI Alignment Assessment
                  </Typography>
                  {selectedIdea.aiAlignmentAssessment ? (
                    <Alert severity="info" icon={<AutoAwesomeIcon />} sx={{ bgcolor: 'rgba(108,155,255,0.08)' }}>
                      <Typography variant="body2">{selectedIdea.aiAlignmentAssessment}</Typography>
                    </Alert>
                  ) : (
                    <Typography variant="body2" color="text.disabled" sx={{ mb: 1 }}>
                      No assessment yet. Run AI assessment to see how this idea aligns with your strategic drivers.
                    </Typography>
                  )}

                  {selectedIdea.suggestedDriver && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">Suggested driver:</Typography>
                      <Chip label={selectedIdea.suggestedDriver.title} size="small" sx={{ bgcolor: 'rgba(129,199,132,0.2)' }} />
                    </Stack>
                  )}
                  {selectedIdea.suggestedProject && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Suggested project:</Typography>
                      <Chip label={selectedIdea.suggestedProject.title} size="small" sx={{ bgcolor: 'rgba(255,183,77,0.2)' }} />
                    </Stack>
                  )}
                </Box>

                {assessError && <Alert severity="error">{assessError}</Alert>}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Button onClick={() => setDetailOpen(false)}>Close</Button>
              {selectedIdea.status !== 'Converted' && selectedIdea.status !== 'Archived' && (
                <>
                  <Button
                    startIcon={assessing ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                    onClick={handleAssess}
                    disabled={assessing}
                    variant="outlined"
                  >
                    {selectedIdea.aiAlignmentAssessment ? 'Re-assess' : 'Assess with AI'}
                  </Button>
                  <Button
                    startIcon={<ConvertIcon />}
                    onClick={() => { setDetailOpen(false); openConvert(selectedIdea); }}
                    variant="contained"
                  >
                    Convert to Work Item
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Convert dialog */}
      <Dialog open={convertOpen} onClose={() => setConvertOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Convert to Work Item</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              value={convertTitle}
              onChange={e => setConvertTitle(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Priority"
              select
              value={convertPriority}
              onChange={e => setConvertPriority(e.target.value)}
              fullWidth
              size="small"
              SelectProps={{ native: true }}
            >
              {['Critical', 'High', 'Medium', 'Low'].map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </TextField>
            <TextField
              label="Estimated minutes"
              type="number"
              value={convertMinutes}
              onChange={e => setConvertMinutes(e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConvert} disabled={converting}>
            {converting ? 'Converting…' : 'Convert'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
